// src/pages/auth/ResetPasswordPage.tsx
// Reset password with token from URL.

import { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useAuthnAuth, useI18n } from '@react/hooks';
import { Button, Input, Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter, Alert, ErrorAlert } from '@react/components';
import { validateNewPassword, calculatePasswordStrength, passwordStrengthLabels } from '../../features/authn/logic';
import { PasswordStrength } from '../../components/PasswordStrength';
import { AuthLayout } from './AuthLayout';

export const ResetPasswordPage = observer(() => {
  const authnAuth = useAuthnAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';

  const [newPassword, setNewPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [errors, setErrors] = useState<Record<string, unknown>>({});
  const [success, setSuccess] = useState(false);

  const passwordScore = calculatePasswordStrength(newPassword);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validation = validateNewPassword({ token, newPassword, passwordConfirmation });
    if (Object.keys(validation).length > 0) {
      setErrors(Object.fromEntries(Object.entries(validation).map(([k, v]) => [k, t(v)])));
      return;
    }

    try {
      await authnAuth.ResetPassword({ token, new_password: newPassword, password_confirmation: passwordConfirmation });
      setSuccess(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      setErrors({ form: err });
    }
  };

  if (!token) {
    return (
      <AuthLayout>
        <Card className="w-full max-w-md">
          <CardContent className="py-8">
            <Alert variant="error">{t('auth.invalidResetToken')}</Alert>
            <Link to="/forgot-password" className="text-sm text-primary hover:text-primary/80 mt-4 block text-center">
              {t('auth.requestNewLink')}
            </Link>
          </CardContent>
        </Card>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle as="h1" className="text-2xl">{t('auth.resetPasswordTitle')}</CardTitle>
          <CardDescription>{t('auth.resetPasswordSubtitle')}</CardDescription>
        </CardHeader>

        {success ? (
          <CardContent className="space-y-4">
            <Alert variant="success">{t('auth.passwordResetSuccess')}</Alert>
            <p className="text-sm text-muted-foreground text-center">{t('auth.redirectingToLogin')}</p>
          </CardContent>
        ) : (
          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-4">
              {errors.form ? (
                <ErrorAlert error={errors.form} dismissible onDismiss={() => setErrors((p) => ({ ...p, form: null }))} />
              ) : null}
              <div className="space-y-1">
                <div>
                  <Input
                    label={t('auth.newPassword')}
                    type="password"
                    value={newPassword}
                    onChange={(e) => { setNewPassword(e.target.value); setErrors({}); }}
                    autoComplete="new-password"
                    error={errors.newPassword as string | undefined}
                    required
                  />
                  <div className={`mt-2${newPassword ? '' : ' invisible'}`}>
                    <PasswordStrength score={passwordScore} label={t(passwordStrengthLabels[passwordScore])} />
                  </div>
                </div>
                <Input
                  label={t('auth.confirmPassword')}
                  type="password"
                  value={passwordConfirmation}
                  onChange={(e) => { setPasswordConfirmation(e.target.value); setErrors({}); }}
                  autoComplete="new-password"
                  error={errors.passwordConfirmation as string | undefined}
                  required
                />
              </div>
            </CardContent>
            <CardFooter className="flex-col space-y-4">
              <Button type="submit" fullWidth isLoading={authnAuth.isLoading}>
                {t('auth.resetPassword')}
              </Button>
            </CardFooter>
          </form>
        )}
      </Card>
    </AuthLayout>
  );
});

export default ResetPasswordPage;
